import { getAllWidgetTags, getWidgetByTagName } from '../widget-registry';
import { WIDGET_SNAPSHOT_CLASS } from './widget-editor.constants';
import { createInertContainer, stripExternalCssUrls } from '../helpers/report-html-sanitizer.helper';

const UNSAFE_TAGS = ['script', 'template', 'noscript', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form', 'style', 'svg', 'math'];
const URL_ATTRS = ['href', 'src', 'xlink:href', 'formaction', 'action'];
const UNSAFE_URL_SCHEMES = ['javascript:', 'data:'];

function encodeWidgetAttrs(el: Element): string {
    const attrs: Record<string, string> = {};
    for (const attr of Array.from(el.attributes)) {
        if (attr.name === 'class') continue;
        attrs[attr.name] = attr.value;
    }
    return escapeHtmlAttr(JSON.stringify(attrs));
}

function escapeHtmlAttr(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// The snapshot is a full HTML fragment (tables, inline styles). Embedding it in a data-attribute
// and round-tripping it through the editor's innerHTML/regex passes corrupts raw < > " characters.
// Base64 (UTF-8 safe) reduces it to [A-Za-z0-9+/=], which survives every pass untouched.
export function encodeSnapshotHtml(html: string): string {
    if (!html) return '';
    const bytes = new TextEncoder().encode(html);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

// Browsers ignore control characters and spaces (U+0000 to U+0020) around a URL scheme, so
// `&#1;javascript:` still runs; drop them all before checking the scheme.
function removeUrlControlChars(value: string): string {
    return Array.from(value).filter(ch => ch.charCodeAt(0) > 0x20).join('');
}

export function sanitizeWidgetHtml(html: string): string {
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return '';

    root.querySelectorAll(UNSAFE_TAGS.join(',')).forEach(el => el.remove());

    root.querySelectorAll('*').forEach(el => {
        Array.from(el.attributes).forEach(attr => {
            const name = attr.name.toLowerCase();
            const value = removeUrlControlChars(attr.value).toLowerCase();
            const isUnsafeUrl = URL_ATTRS.includes(name) && UNSAFE_URL_SCHEMES.some(scheme => value.startsWith(scheme));
            if (name.startsWith('on') || isUnsafeUrl) {
                el.removeAttribute(attr.name);
            } else if (name === 'style') {
                el.setAttribute(attr.name, stripExternalCssUrls(attr.value));
            }
        });
    });

    return root.innerHTML;
}

export function preprocessWidgetsForEditor(html: string, snapshotMap?: Map<string, string[]>): string {
    const container = createInertContainer();
    container.innerHTML = html;

    for (const tag of getAllWidgetTags()) {
        const registration = getWidgetByTagName(tag);
        const loopTemplate = registration?.loopTemplate;
        const snapshots = snapshotMap?.get(tag);
        container.querySelectorAll(tag).forEach((el: Element, index: number) => {
            if (registration?.editorAtomic) {
                // Prefer the widget's "template mode" schematic (styled table + placeholder chips +
                // loop markers) over a frozen real-data snapshot, so the editor reads as a template.
                const rawSnapshot = registration.editorSchematicHtml ?? snapshots?.[index] ?? '';
                const snapshotHtml = encodeSnapshotHtml(sanitizeWidgetHtml(rawSnapshot));
                const wrapper = document.createElement('div');
                wrapper.innerHTML = `<div class="${WIDGET_SNAPSHOT_CLASS}" data-widget-tag="${tag}" data-widget-attrs="${encodeWidgetAttrs(el)}" data-snapshot="${snapshotHtml}"></div>`;
                const replacement = wrapper.firstChild;
                if (replacement) el.parentNode?.replaceChild(replacement, el);
                return;
            }
            const replacementHtml = loopTemplate ?? (snapshots?.[index] ? sanitizeWidgetHtml(snapshots[index]) : '');
            if (!replacementHtml.trim()) return;
            const temp = document.createElement('div');
            temp.innerHTML = replacementHtml;
            const fragment = document.createDocumentFragment();
            Array.from(temp.childNodes).forEach(node => fragment.appendChild(node));
            el.parentNode?.replaceChild(fragment, el);
        });
    }

    return container.innerHTML;
}
